import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged } from 'rxjs';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { InstitutionDetailsView } from './institution-details-view/institution-details-view';
import { LvrHeading } from '../../../core/components/heading/heading';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { InstitutionExtended } from '../../../api/app-types/institution.app';
import { InstitutionActivity } from '../institution-activity/institution-activity';

@Component({
	selector: 'lvr-institution-details',
	templateUrl: './institution-details.html',
	styleUrl: './institution-details.scss',
	imports: [
		TranslocoPipe,
		MatTooltipModule,
		MatIconModule,
		MatButtonModule,
		InstitutionDetailsView,
		LvrHeading,
		InstitutionActivity,
	],
})
export class InstitutionDetails {
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly session = inject(SessionManagerService);
	private readonly data = toSignal(this.route.data.pipe(distinctUntilChanged()));
	private title = signal<string>('');
	public readonly institution = computed<InstitutionExtended>(() => this.data()?.['institution']);
	private readonly hasInstitutionEditPermission = hasPermission(this.session.permissions, 'Institutions.Edit');
	private readonly isSchool = computed(() => this.institution().schoolNumber !== undefined);
	public readonly canEditInstitution = computed(() => this.hasInstitutionEditPermission() && !this.isSchool());

	public constructor() {
		effect(() => {
			const institution = this.institution();
			if (institution.name !== this.title()) {
				this.title.set(institution.name);
				this.recordManager.updateRecordTitleOfActiveRecord(this.title());
			}
		});
	}

	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}
}
